import { chainContaining } from '@/lib/text-linking/text-chain-mutator'
import type { TextChain } from '@/api/types/stowed-element'
import type { GroupCandidateTextBox } from './types/group-candidate-text-box'

/**
 * True when this frame belongs to a link chain, and so must never be pulled into a group
 * (WMPCM-6948). Grouping re-parents a child (`parentGroupId`) and hands its geometry to the group
 * shell, which breaks the chain's reflow assumptions. Applies equally to a Text-tool frame and to a
 * shape converted to a text frame (WMPCM-7988).
 *
 * Chain membership is extrinsic — the frame carries only a `uid`, and the chain list keyed by that
 * uid is the source of truth (see `text-chain-mutator.ts`). A frame with no `uid` was never linked.
 *
 * **This is the one definition; `isLinkedChainMember` (`lib/editor/cut-selection-policy.ts`) and
 * `isPhotoDropBlockedByTextChain` (`lib/photos/linked-text-photo-drop-policy.ts`) both delegate to
 * it.** Keep it that way — three predicates drifting apart is what WMPCM-8969 was.
 *
 * A non-positive `uid` counts as unlinked, not as chain key `"0"`. **0 means "unset" on the wire**,
 * and both load paths already normalise it away (`checkpoint-to-shapes.ts`,
 * `use-multiplayer-elements-sync.ts`), as does minting (`ensureFrameUid`) — so nothing reaching here
 * should carry one. The guard states that invariant rather than trusting it: a `uid: 0` frame
 * matching a chain that also lost its uids would read as linked and be refused a group forever.
 */
export function isLinkedTextFrame(
  box: GroupCandidateTextBox,
  chains: readonly TextChain[] | undefined
): boolean {
  if (typeof box.uid !== 'number' || box.uid <= 0) return false
  return chainContaining((chains ?? []) as TextChain[], String(box.uid)) != null
}

/** Shown when a group was created but linked frames were left out of it (WMPCM-6948 TC-01/02). */
export const LINKED_TEXT_EXCLUDED_FROM_GROUP_MESSAGE =
  'Group created. Linked text frames were excluded.'

/**
 * Shown when dropping the linked frames leaves fewer than two groupable objects, so no group is
 * created at all (WMPCM-6948 TC-03/05).
 */
export const LINKED_TEXT_GROUP_BLOCKED_MESSAGE =
  'Cannot group linked text frames. Unlink them first.'
